import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '@/services/auth';
import { api } from '@/services/api';
import type { User, Organization } from '@/types';

interface AuthState {
  user: User | null;
  organizations: Organization[];
  currentOrganization: Organization | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasHydrated: boolean;
  error: string | null;
  // Clerk parked the sign-in pending a new-device email code.
  needsSecondFactor: boolean;

  login: (email: string, password: string) => Promise<void>;
  verifySecondFactor: (code: string) => Promise<void>;
  resendSecondFactorCode: () => Promise<void>;
  signup: (email: string, password: string) => Promise<{ needsEmailConfirmation: boolean }>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  checkAuth: () => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  switchOrganization: (org: Organization) => void;
  refreshOrganization: () => Promise<void>;
  clearError: () => void;
  setHasHydrated: (state: boolean) => void;
}

function setupOrganization(orgs: Organization[]): Organization | null {
  if (orgs.length === 0) return null;

  const savedOrgId = api.organizationId;
  if (savedOrgId) {
    const savedOrg = orgs.find((o) => String(o.id) === savedOrgId);
    if (savedOrg) {
      api.setOrganizationId(savedOrg.id);
      return savedOrg;
    }
  }

  const defaultOrg = orgs[0];
  api.setOrganizationId(defaultOrg.id);
  return defaultOrg;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      organizations: [],
      currentOrganization: null,
      isAuthenticated: false,
      isLoading: true,
      hasHydrated: false,
      error: null,
      needsSecondFactor: false,

      setHasHydrated: (state: boolean) => set({ hasHydrated: state }),

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const { session, needsSecondFactor } = await authService.signIn({
            email,
            password,
          });

          // Clerk wants a new-device code before minting a session.
          if (needsSecondFactor) {
            set({ needsSecondFactor: true, isLoading: false });
            return;
          }

          if (session) {
            const { user, organizations } = await api.verifyAuth();
            const currentOrganization = setupOrganization(organizations);
            set({
              user,
              organizations,
              currentOrganization,
              isAuthenticated: true,
              isLoading: false,
            });
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Login failed';
          set({ error: message, isLoading: false });
          throw err;
        }
      },

      verifySecondFactor: async (code: string) => {
        set({ isLoading: true, error: null });
        try {
          await authService.verifySecondFactorCode(code);
          const { user, organizations } = await api.verifyAuth();
          const currentOrganization = setupOrganization(organizations);
          set({
            user,
            organizations,
            currentOrganization,
            isAuthenticated: true,
            needsSecondFactor: false,
            isLoading: false,
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Verification failed';
          set({ error: message, isLoading: false });
          throw err;
        }
      },

      resendSecondFactorCode: async () => {
        set({ error: null });
        try {
          await authService.resendSecondFactorCode();
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to resend code';
          set({ error: message });
          throw err;
        }
      },

      signup: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const { needsEmailConfirmation } = await authService.signUp({ email, password });

          // If no verification required, session is already active
          if (!needsEmailConfirmation) {
            const { user, organizations } = await api.verifyAuth();
            const currentOrganization = setupOrganization(organizations);
            set({
              user,
              organizations,
              currentOrganization,
              isAuthenticated: true,
              isLoading: false,
            });
            return { needsEmailConfirmation: false };
          } else {
            // Email confirmation required
            set({ isLoading: false });
            return { needsEmailConfirmation: true };
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Signup failed';
          set({ error: message, isLoading: false });
          throw err;
        }
      },

      logout: async () => {
        await authService.signOut();
        api.setToken(null);
        api.setOrganizationId(null);
        set({
          user: null,
          organizations: [],
          currentOrganization: null,
          isAuthenticated: false,
          error: null,
        });
      },

      deleteAccount: async () => {
        try {
          await api.deleteAccount();
          await authService.signOut();
          api.setToken(null);
          api.setOrganizationId(null);
          set({
            user: null,
            organizations: [],
            currentOrganization: null,
            isAuthenticated: false,
            error: null,
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to delete account';
          set({ error: message });
          throw err;
        }
      },

      checkAuth: async () => {
        set({ isLoading: true });

        try {
          const session = await authService.getSession();

          if (session) {
            // Use verifyAuth to ensure org is created if first login
            const { user, organizations } = await api.verifyAuth();
            const currentOrganization = setupOrganization(organizations);
            set({
              user,
              organizations,
              currentOrganization,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            set({
              user: null,
              organizations: [],
              currentOrganization: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } catch {
          set({
            user: null,
            organizations: [],
            currentOrganization: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      resendVerification: async (_email: string) => {
        // Clerk resends the pending sign-up's email code
        await authService.resendSignupCode();
      },

      switchOrganization: (org: Organization) => {
        api.setOrganizationId(org.id);
        set({ currentOrganization: org });
      },

      refreshOrganization: async () => {
        try {
          const { organizations } = await api.verifyAuth();
          const currentOrgId = api.organizationId;
          const currentOrg = organizations.find((o) => String(o.id) === currentOrgId) || organizations[0];
          set({ organizations, currentOrganization: currentOrg });
        } catch {
          // Silently fail
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'statsmatic-auth',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

// Listen for auth state changes (tokens are fetched fresh per request by
// the api client, so only sign-out needs handling here)
authService.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT') {
    api.setToken(null);
    api.setOrganizationId(null);
    useAuthStore.setState({
      user: null,
      organizations: [],
      currentOrganization: null,
      isAuthenticated: false,
    });
  }
});

export default useAuthStore;
